import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContraEditorContext } from '../../context/ContraEditorContext.js';
import { createCustomBlockSpec } from '../../routines/createCustomBlockSpec.js';
import { getMediaUrl } from '../../routines/getMediaUrl.js';
import { maybeAppendProtocol } from '../../routines/maybeAppendProtocol.js';
import { getEmbedComponent } from './utilities.js';
import { EditIcon } from '@contra/icons/icons';
import { useCallback, useEffect, useMemo, useState, } from 'react';
import { extractContentInfoFromUrl } from 'semantic-url-parser';
const blockConfig = {
    content: 'none',
    propSchema: {
        coverImageAnimated: {
            default: false,
        },
        coverImageChecksum: {
            default: '',
        },
        coverImageHeight: {
            default: 0,
        },
        coverImageOriginalFormat: {
            default: '',
        },
        coverImageSizeBytes: {
            default: 0,
        },
        coverImageUid: {
            default: '',
        },
        coverImageUrl: {
            default: '',
        },
        coverImageWidth: {
            default: 0,
        },
        placeholderId: {
            default: '',
        },
        url: {
            default: '',
        },
    },
    type: 'embed',
};
const DefaultEmbedElement = ({ coverImageUid, coverImageUrl, onEditCoverImage, url, }) => {
    const [title, setTitle] = useState(null);
    const [description, setDescription] = useState(null);
    const [image, setImage] = useState(undefined);
    const [embedUrl, setEmbedUrl] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const { fetchWebPageMetadata } = useContraEditorContext();
    const fetchWebPageMetadataWithTimeout = useCallback(async () => {
        setIsLoading(true);
        const start = Date.now();
        while (Date.now() - start < 60_000) {
            const result = await fetchWebPageMetadata(url);
            if (result) {
                setTitle(result.title ?? null);
                setDescription(result.description ?? null);
                setImage(result.openGraphImage?.uid
                    ? `https://media.contra.com/image/upload/ar_1.91,c_fill,fl_lossy,w_1200,q_auto:best/${result.openGraphImage.uid}.avif`
                    : undefined);
                setEmbedUrl(result.embedUrl ?? undefined);
                break;
            }
            else {
                await new Promise((resolve) => {
                    setTimeout(resolve, 1_000);
                });
            }
        }
        setIsLoading(false);
    }, [fetchWebPageMetadata, url]);
    useEffect(() => {
        fetchWebPageMetadataWithTimeout();
    }, []);
    if (isLoading) {
        return null;
    }
    if (embedUrl) {
        return (_jsx("iframe", { src: embedUrl, style: {
                border: 'none',
                borderRadius: 6,
                boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)',
                height: 500,
                overflow: 'hidden',
                width: '100%',
            } }));
    }
    if (!title) {
        return _jsx("a", { href: url, children: url });
    }
    return (_jsx("div", { className: "contra-default-embed-wrapper", children: _jsxs("a", { className: "contra-default-embed", href: url, rel: "noreferrer", target: "_blank", children: [_jsxs("div", { className: "contra-default-embed-left", onClick: onEditCoverImage, children: [_jsx("img", { className: "contra-default-embed-image", src: coverImageUrl
                                ? coverImageUrl
                                : coverImageUid
                                    ? getMediaUrl({
                                        assetType: 'image',
                                        format: 'avif',
                                        uid: coverImageUid,
                                    })
                                    : image }), onEditCoverImage ? (_jsx("button", { className: "contra-default-embed-edit-button", type: "button", children: _jsx(EditIcon, { color: "#373F51", size: 24 }) })) : null] }), _jsxs("div", { className: "contra-default-embed-right", children: [_jsxs("div", { className: "contra-default-embed-title-and-description", children: [_jsx("div", { className: "contra-default-embed-title", children: title }), _jsx("div", { className: "contra-default-embed-description", children: description ?? '' })] }), _jsx("div", { className: "contra-default-embed-url", children: url })] })] }) }));
};
const EmbedElement = ({ block, onEditCoverImage, }) => {
    const { coverImageUid, coverImageUrl, url } = block.props;
    const contentInfo = useMemo(() => {
        try {
            return extractContentInfoFromUrl(maybeAppendProtocol(url));
        }
        catch {
            return null;
        }
    }, [url]);
    if (contentInfo) {
        const Component = getEmbedComponent(contentInfo);
        if (Component) {
            return (_jsx(Component, { contentInfo: contentInfo, originalUrl: url }));
        }
    }
    return (_jsx(DefaultEmbedElement, { coverImageUid: coverImageUid, coverImageUrl: coverImageUrl, onEditCoverImage: onEditCoverImage, url: url }));
};
const EditableEmbedElement = ({ block, editor, }) => {
    const handleEditCoverImage = useCallback((event) => {
        event.stopPropagation();
        event.preventDefault();
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = [
            'image/avif',
            'image/webp',
            'image/png',
            'image/jpeg',
            'image/gif',
        ].join(',');
        input.onchange = async (inputEvent) => {
            const target = inputEvent.target;
            const file = target?.files?.[0];
            if (!file) {
                return;
            }
            const result = await editor.uploadFile?.(file);
            if (typeof result === 'object') {
                editor.updateBlock(block.id, {
                    props: {
                        coverImageUrl: result.props.url,
                        placeholderId: result.props.placeholderId,
                    },
                });
            }
        };
        input.click();
    }, [block.id, editor]);
    return (_jsx(EmbedElement, { block: block, onEditCoverImage: handleEditCoverImage }));
};
export const Embed = createCustomBlockSpec(blockConfig, {
    render: (props) => {
        if (props.readOnly) {
            return (_jsx("div", { style: {
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                }, children: _jsx(EmbedElement, { block: props.block }) }));
        }
        return (_jsx("div", { style: {
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
            }, children: _jsx(EditableEmbedElement, { block: props.block, editor: props.editor }) }));
    },
    toExternalHTML: ({ block }) => {
        return (_jsx("p", { children: _jsx("a", { href: block?.props?.url, children: block?.props?.url }) }));
    },
});
//# sourceMappingURL=Embed.js.map